import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, setDoc, collection, getDocs, writeBatch, query, getDoc } from 'firebase/firestore';

/**
 * Resolve a title through chained mappings.
 * Includes cycle detection to avoid infinite loops.
 */
export const applyTitleMapping = (title, mappings) => {
  if (!title || !mappings || Object.keys(mappings).length === 0) return title;
  let resolved = title;
  const seen = new Set();
  while (mappings[resolved] && !seen.has(resolved)) {
    seen.add(resolved);
    resolved = mappings[resolved];
  }
  return resolved;
};

export const fetchTitleMappings = async (userId) => {
  if (!userId) return {};
  try {
    const ref = doc(db, "users", userId, "settings", "titleMappings");
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return snap.data().mappings || {};
    }
    return {};
  } catch (error) {
    console.error("Error fetching title mappings:", error);
    return {};
  }
};

const TitleManagementPanel = ({
  user,
  getAllUniqueTitles,
  sessionHistory,
  setSessionHistory,
  hasBasicAccess,
  onTitleMappingsUpdated
}) => {
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(null);
  const [newTitleName, setNewTitleName] = useState('');
  const [mergeMode, setMergeMode] = useState(false);
  const [sourceTitle, setSourceTitle] = useState('');
  const [targetTitle, setTargetTitle] = useState('');
  const [processing, setProcessing] = useState(false);
  const [titleCounts, setTitleCounts] = useState({});
  const [titleMappings, setTitleMappings] = useState({});
  const [searchFilter, setSearchFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState('count'); // 'title' | 'count'
  const [sortDir, setSortDir] = useState('desc');       // 'asc' | 'desc'
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (user?.uid && hasBasicAccess) {
      loadTitleMappings().then(() => {
        loadTitles();
      });
    } else {
      loadTitles();
    }
  }, [sessionHistory, user?.uid]);

  const loadTitleMappings = async () => {
    if (!user?.uid || !hasBasicAccess) {
      setTitleMappings({});
      return;
    }
    try {
      const mappings = await fetchTitleMappings(user.uid);
      setTitleMappings(mappings);
      return mappings;
    } catch (error) {
      console.error("Error loading title mappings:", error);
      setTitleMappings({});
      return {};
    }
  };

  const applyLocalMappings = (titleList) => {
    if (!titleMappings || Object.keys(titleMappings).length === 0) {
      return titleList;
    }

    const seen = new Set();
    const result = [];

    titleList.forEach(t => {
      const mapped = applyTitleMapping(t.name, titleMappings);
      if (!seen.has(mapped)) {
        seen.add(mapped);
        result.push({ ...t, name: mapped });
      } else {
        const existing = result.find(r => r.name === mapped);
        if (existing) existing.count += t.count;
      }
    });

    return result;
  };

  const loadTitles = () => {
    setLoading(true);
    try {
      const titleObjects = getAllUniqueTitles();
      const counts = {};
      titleObjects.forEach(t => { counts[t.name] = t.count; });

      const mapped = applyLocalMappings(titleObjects);
      mapped.sort((a, b) => b.count - a.count);

      setTitles(mapped);
      setTitleCounts(counts);
    } catch (error) {
      console.error('Error loading titles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTitle = (title) => {
    setEditingTitle(title);
    setNewTitleName(title.name);
    setMergeMode(false);
  };

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setNewTitleName('');
  };

  const handleSaveEdit = async () => {
    if (!newTitleName.trim() || newTitleName === editingTitle.name) {
      handleCancelEdit();
      return;
    }
    if (!user?.uid || !hasBasicAccess) {
      alert('You need a Basic or Pro subscription to edit titles.');
      handleCancelEdit();
      return;
    }

    setProcessing(true);
    try {
      await updateTitleInSessions(editingTitle.name, newTitleName.trim());
      await updateTitleMappingInFirestore(editingTitle.name, newTitleName.trim());

      setTitles(prev =>
        prev.map(t =>
          t.name === editingTitle.name ? { ...t, name: newTitleName.trim() } : t
        )
      );
      handleCancelEdit();
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Failed to update title: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleMergeMode = () => {
    setMergeMode(!mergeMode);
    setEditingTitle(null);
    setSourceTitle('');
    setTargetTitle('');
  };

  const handleMergeTitles = async () => {
    if (!sourceTitle || !targetTitle || sourceTitle === targetTitle) {
      alert('Please select different source and target titles.');
      return;
    }
    if (!user?.uid || !hasBasicAccess) {
      alert('You need a Basic or Pro subscription to merge titles.');
      return;
    }
    if (!window.confirm(`Are you sure you want to merge "${sourceTitle}" into "${targetTitle}"? This cannot be undone.`)) {
      return;
    }

    setProcessing(true);
    try {
      await updateTitleInSessions(sourceTitle, targetTitle);
      await updateTitleMappingInFirestore(sourceTitle, targetTitle);

      setTitles(prev =>
        prev
          .filter(t => t.name !== sourceTitle)
          .map(t =>
            t.name === targetTitle
              ? { ...t, count: (titleCounts[targetTitle] || 0) + (titleCounts[sourceTitle] || 0) }
              : t
          )
      );

      setMergeMode(false);
      setSourceTitle('');
      setTargetTitle('');
      alert(`Successfully merged "${sourceTitle}" into "${targetTitle}".`);
    } catch (error) {
      console.error('Error merging titles:', error);
      alert('Failed to merge titles: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const updateTitleMappingInFirestore = async (oldTitle, newTitle) => {
    if (!user?.uid || !hasBasicAccess) return;

    try {
      const ref = doc(db, "users", user.uid, "settings", "titleMappings");
      const snap = await getDoc(ref);
      let mappings = {};

      if (snap.exists()) {
        mappings = snap.data().mappings || {};
      }

      mappings[oldTitle] = newTitle;

      await setDoc(ref, { mappings, updatedAt: new Date() }, { merge: true });

      if (typeof onTitleMappingsUpdated === 'function') {
        onTitleMappingsUpdated(mappings);
      }
    } catch (error) {
      console.error('Error updating title mapping in Firestore:', error);
      throw error;
    }
  };

  const updateTitleInSessions = async (oldTitle, newTitle) => {
    const sessionContainsTitle = (session, titleToFind) => {
      if (!session.items || !Array.isArray(session.items)) return false;
      const checkItem = (item) => {
        if (item.title === titleToFind) return true;
        if (item.children && Array.isArray(item.children)) {
          return item.children.some(child => checkItem(child));
        }
        return false;
      };
      return session.items.some(item => checkItem(item));
    };

    const updatedHistory = sessionHistory.map(session => {
      if (!session.items) return session;

      const sessionCopy = { ...session, items: JSON.parse(JSON.stringify(session.items)) };
      let sessionUpdated = false;

      const updateItemTitle = (item) => {
        let itemUpdated = false;
        if (item.title === oldTitle) {
          item.title = newTitle;
          itemUpdated = true;
        }
        if (item.children && item.children.length > 0) {
          item.children.forEach(child => {
            if (updateItemTitle(child)) itemUpdated = true;
          });
        }
        return itemUpdated;
      };

      sessionCopy.items.forEach(item => {
        if (updateItemTitle(item)) sessionUpdated = true;
      });

      return sessionUpdated ? sessionCopy : session;
    });

    setSessionHistory(updatedHistory);

    if (user?.uid && hasBasicAccess) {
      const batch = writeBatch(db);

      for (let i = 0; i < sessionHistory.length; i++) {
        const originalSession = sessionHistory[i];
        const updatedSession = updatedHistory[i];

        if (sessionContainsTitle(originalSession, oldTitle)) {
          const sessionRef = doc(db, "users", user.uid, "sessionHistory", updatedSession.id);
          batch.set(sessionRef, updatedSession);
        }
      }

      const plansQuery = query(collection(db, "users", user.uid, "practicePlans"));
      const plansSnapshot = await getDocs(plansQuery);

      plansSnapshot.forEach(planDoc => {
        const plan = planDoc.data();
        if (!plan.items || !Array.isArray(plan.items)) return;

        const planCopy = { ...plan, items: JSON.parse(JSON.stringify(plan.items)) };
        let planUpdated = false;

        const updateItemTitle = (item) => {
          let itemUpdated = false;
          if (item.title === oldTitle) {
            item.title = newTitle;
            itemUpdated = true;
          }
          if (item.children && item.children.length > 0) {
            item.children.forEach(child => {
              if (updateItemTitle(child)) itemUpdated = true;
            });
          }
          return itemUpdated;
        };

        planCopy.items.forEach(item => {
          if (updateItemTitle(item)) planUpdated = true;
        });

        if (planUpdated) {
          batch.set(planDoc.ref, planCopy);
        }
      });

      await batch.commit();
    }
  };

  const filteredTitles = (() => {
    let result = searchFilter.trim()
      ? titles.filter(t => t.name.toLowerCase().includes(searchFilter.trim().toLowerCase()))
      : [...titles];

    result.sort((a, b) => {
      if (sortField === 'title') {
        const cmp = a.name.localeCompare(b.name);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const cmp = a.count - b.count;
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  })();

  const totalPages = Math.ceil(filteredTitles.length / PAGE_SIZE);
  const paginatedTitles = filteredTitles.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const handleSearchChange = (value) => {
    setSearchFilter(value);
    setCurrentPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'title' ? 'asc' : 'desc');
    }
    setCurrentPage(0);
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <p className="text-sm text-gray-600">
          {titles.length} unique title{titles.length !== 1 ? 's' : ''} found
          {searchFilter.trim() && filteredTitles.length !== titles.length && (
            <span className="ml-1">({filteredTitles.length} matching filter)</span>
          )}
        </p>

        <div className="flex space-x-2">
          {!mergeMode ? (
            <button
              onClick={handleToggleMergeMode}
              className="badge badge-primary text-xs rounded-md transition-colors hover:bg-brand-100"
              disabled={titles.length < 2 || processing}
            >
              Merge Titles
            </button>
          ) : (
            <button
              onClick={handleToggleMergeMode}
              className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
              disabled={processing}
            >
              Cancel Merge
            </button>
          )}
        </div>
      </div>

      {mergeMode && (
        <div className="mb-4 p-3 bg-brand-50 border border-brand-200 rounded-lg">
          <h4 className="font-medium mb-2">Merge Titles</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-700">
                Source Title (will be removed)
              </label>
              <select
                value={sourceTitle}
                onChange={(e) => setSourceTitle(e.target.value)}
                className="w-full p-1.5 text-sm border border-gray-300 rounded"
                disabled={processing}
              >
                <option value="">Select a title</option>
                {titles.map(t => (
                  <option
                    key={`source-${t.name}`}
                    value={t.name}
                    disabled={t.name === targetTitle}
                  >
                    {t.name} ({t.count})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-700">
                Target Title (will remain)
              </label>
              <select
                value={targetTitle}
                onChange={(e) => setTargetTitle(e.target.value)}
                className="w-full p-1.5 text-sm border border-gray-300 rounded"
                disabled={processing}
              >
                <option value="">Select a title</option>
                {titles.map(t => (
                  <option
                    key={`target-${t.name}`}
                    value={t.name}
                    disabled={t.name === sourceTitle}
                  >
                    {t.name} ({t.count})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleMergeTitles}
              className="px-3 py-1.5 text-sm bg-brand-500 text-white rounded hover:bg-brand-600 disabled:opacity-50"
              disabled={!sourceTitle || !targetTitle || sourceTitle === targetTitle || processing}
            >
              {processing ? 'Merging...' : 'Merge Titles'}
            </button>
          </div>

          <div className="mt-2 text-xs text-gray-500">
            <p>Merging will replace all instances of the source title with the target title.</p>
            <p>This action cannot be undone.</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Loading titles...</span>
        </div>
      ) : titles.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No titles found in your practice sessions.</p>
          <p className="text-sm mt-1">Add titles to your practice blocks to start organizing.</p>
        </div>
      ) : (
        <>
          {/* Search filter */}
          <div className="mb-3">
            <input
              type="text"
              placeholder="Filter titles..."
              value={searchFilter}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
            />
          </div>

          <div className="border border-gray-200 rounded-md overflow-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-surface-sunken">
                <tr>
                  <th
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2 cursor-pointer select-none hover:text-gray-700"
                    onClick={() => handleSort('title')}
                  >
                    Title{sortIndicator('title')}
                  </th>
                  <th
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer select-none hover:text-gray-700"
                    onClick={() => handleSort('count')}
                  >
                    Count{sortIndicator('count')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTitles.map(t => (
                  <tr key={t.name} className={editingTitle?.name === t.name ? 'bg-blue-50' : ''}>
                    <td className="px-3 py-3 text-sm">
                      {editingTitle?.name === t.name ? (
                        <input
                          type="text"
                          value={newTitleName}
                          onChange={(e) => setNewTitleName(e.target.value)}
                          className="p-1 border border-gray-300 rounded w-full"
                          autoFocus
                        />
                      ) : (
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px] md:max-w-none">
                          {t.name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm text-gray-500">{t.count}</div>
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-medium">
                      {editingTitle?.name === t.name ? (
                        <div className="flex space-x-2 justify-end">
                          <button
                            onClick={handleSaveEdit}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            disabled={processing || !newTitleName.trim() || newTitleName === t.name}
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                            disabled={processing}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditTitle(t)}
                          className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                          disabled={processing || mergeMode || !!editingTitle}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500">
                Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, filteredTitles.length)} of {filteredTitles.length}
              </p>
              <div className="flex space-x-1">
                <button
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <span className="px-2 py-1 text-xs text-gray-600">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-4 text-sm text-gray-500">
        <p>Tip: Use title management to standardize your practice block names. For example, change "long tones" to "Long Tones" for consistency.</p>
        <p>Note: Title changes will affect all practice sessions and plans.</p>
      </div>

      {user?.uid && !hasBasicAccess && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
          <p className="text-yellow-800 font-medium">Subscribe to edit and manage titles</p>
          <p className="text-yellow-700 mt-1">Title management requires a Basic or Pro subscription.</p>
        </div>
      )}
    </div>
  );
};

export default TitleManagementPanel;
